
import { useState, useRef } from 'react';
import { Box, useTheme } from '@mui/material';

const OTPInput = ({ length = 6, onChange }) => {
    const theme = useTheme();
    const [otp, setOtp] = useState(Array(length).fill(''));
    const inputsRef = useRef([]);

    const handleChange = (index, value) => {
        // Only allow single digit numbers
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Join OTP and pass to parent
        const otpString = newOtp.join('');
        onChange?.(otpString);

        // Auto-focus next input
        if (value !== '' && index < length - 1) {
            inputsRef.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            e.preventDefault();
            if (otp[index] === '' && index > 0) {
                // Move to previous input and clear it
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                onChange?.(newOtp.join(''));
                inputsRef.current[index - 1].focus();
            } else {
                // Clear current input
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
                onChange?.(newOtp.join(''));
            }
        }

        // Handle left arrow
        if (e.key === 'ArrowLeft' && index > 0) {
            inputsRef.current[index - 1].focus();
        }

        // Handle right arrow
        if (e.key === 'ArrowRight' && index < length - 1) {
            inputsRef.current[index + 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').slice(0, length);

        if (/^\d+$/.test(pasteData)) {
            const newOtp = [...otp];
            pasteData.split('').forEach((char, i) => {
                if (i < length) {
                    newOtp[i] = char;
                }
            });
            setOtp(newOtp);
            onChange?.(newOtp.join(''));

            // Focus the next empty input or last input
            const nextIndex = Math.min(pasteData.length, length - 1);
            inputsRef.current[nextIndex].focus();
        }
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
                mt: 2,
                mb: 3
            }}>
            {otp.map((digit, index) => (
                <Box
                    key={index}
                    sx={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: digit ? theme.palette.primary.main : theme.palette.background.secondary,
                        border: 'none', // No border for empty fields
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        cursor: 'text',
                        boxShadow: digit ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': {
                            backgroundColor: digit ? theme.palette.primary.main : theme.palette.background.secondary,
                        }
                    }}
                    onClick={() => {
                        inputsRef.current[index].focus();
                    }}
                >
                    {/* Hidden input */}
                    <input
                        ref={(el) => (inputsRef.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        onFocus={handleFocus}
                        style={{
                            position: 'absolute',
                            opacity: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            background: 'transparent',
                            outline: 'none',
                            padding: 0,
                            cursor: 'text',
                        }}
                    />

                    {/* Display dot (.) for empty, digit for filled */}
                    <Box
                        component="span"
                        sx={{
                            fontSize: digit ? '24px' : '30px', // Larger dot size (36px)
                            fontWeight: digit ? 700 : 400,
                            color: digit ? 'black' : '#aaaaaa', // White digits on #ECA72E background
                            lineHeight: 1,
                            userSelect: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        {digit || <img src='/Images/otpdot.png' alt='otpdot' />}
                    </Box>

                    {/* Show cursor in empty active input */}
                    {!digit && document.activeElement === inputsRef.current[index] && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '2px',
                                height: '24px',
                                backgroundColor: '#666666',
                                animation: 'blink 1s infinite',
                                '@keyframes blink': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0 },
                                }
                            }}
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default OTPInput;